import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AppLayout } from '@/components/layout/AppLayout';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Bookmark, ExternalLink, Tag } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface BookmarkedTool {
  tool_id: string;
  created_at: string;
  tool: {
    id: string;
    name: string;
    description: string;
    icon: string;
    category: {
      name: string;
    };
    tags: Array<{
      tags: {
        name: string;
        color: string;
      };
    }>;
  };
}

const Bookmarks: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const { toast } = useToast();

  const { data: bookmarkedTools, isLoading, refetch } = useQuery({
    queryKey: ['bookmarked-tools', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('tool_bookmarks')
        .select(`
          tool_id,
          created_at,
          tool:tools(
            id,
            name,
            description,
            icon,
            category:categories(name),
            tags:tool_tags(tags(name, color))
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BookmarkedTool[];
    },
    enabled: !!user,
  });

  const removeBookmark = async (toolId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('tool_bookmarks')
        .delete()
        .eq('tool_id', toolId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      refetch();
      toast({
        title: "Bookmark Removed",
        description: "Tool removed from your bookmarks.",
      });
    } catch (error) {
      console.error('Error removing bookmark:', error);
      toast({
        title: "Error",
        description: "Failed to remove bookmark. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return (
      <AppLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-muted-foreground">Please sign in to view your bookmarked tools.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <AppHeader
        title="Bookmarked Tools"
        subtitle="Access your saved tools and templates for quick reference and reuse."
        breadcrumbs={[
          { label: 'Bookmarked Tools' }
        ]}
      />
      
      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-full"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : bookmarkedTools && bookmarkedTools.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bookmarkedTools.map((bookmark) => (
              <Card key={bookmark.tool_id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">{bookmark.tool.icon}</div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{bookmark.tool.name}</CardTitle>
                        <CardDescription className="text-sm mt-1">
                          {bookmark.tool.description}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-3">
                    <Badge variant="secondary" className="text-xs">
                      {bookmark.tool.category?.name || 'General'}
                    </Badge>
                    {bookmark.tool.tags?.slice(0, 2).map((tagItem, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className="text-xs"
                        style={{ 
                          borderColor: tagItem.tags.color,
                          color: tagItem.tags.color 
                        }}
                      >
                        <Tag className="w-2 h-2 mr-1" />
                        {tagItem.tags.name}
                      </Badge>
                    ))}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/tool/${bookmark.tool.id}`)}
                      className="flex-1"
                      size="sm"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Tool
                    </Button>
                    <Button
                      onClick={() => removeBookmark(bookmark.tool_id)}
                      variant="outline"
                      size="sm"
                    >
                      <Bookmark className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    Bookmarked {new Date(bookmark.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Bookmark className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Bookmarked Tools</h3>
            <p className="text-muted-foreground mb-4">
              You haven't bookmarked any tools yet. Start exploring tools and save your favorites for quick access.
            </p>
            <Button onClick={() => navigate(prefix)} variant="outline">
              Explore Tools
            </Button>
          </div>
        )}
      </div>
      
      <Footer />
    </AppLayout>
  );
};

export default Bookmarks;